"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useEffect, useState } from "react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious, type CarouselApi } from "@/components/ui/carousel";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Progress } from "@/components/ui/progress";
import { Quote, MapPin } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const testimonials = [{
  id: 1,
  quote: "The support given through this foundation is helping children in our community access better opportunities for learning and growth.",
  author: "Community Representative",
  role: "Local Partner",
  location: "Central Region, Ghana",
  image: "https://farquharfisherfoundation.co.uk/wp-content/uploads/2026/03/99.jpeg?v=bzxlbmco4o"
}, {
  id: 2,
  quote: "The ICT Centre has created new opportunities for children and young people to learn practical digital skills and build confidence for the future.",
  author: "School Representative",
  role: "Education Partner",
  location: "Ajumako Baa, Ghana",
  image: "https://farquharfisherfoundation.co.uk/wp-content/uploads/2026/04/WhatsApp-Image-2026-04-10-at-21.21.41.jpeg?v=en6yxlftqtq"
}, {
  id: 3,
  quote: "Providing transport support for school children has made daily school attendance easier and safer for many families in the area.",
  author: "Parent Representative",
  role: "Parent",
  location: "Central Region, Ghana",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1602004/img/testimonial_kofi_b.png"
}, {
  id: 4,
  quote: "It is encouraging to see a new organisation already making meaningful progress through practical educational and community-based projects.",
  author: "Supporter",
  role: "Donor",
  location: "United Kingdom",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1602004/img/testimonial_sarah_m.png"
}];
export default function StoriesOfCareAndChange() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!api) return;
    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap() + 1);
    const onSelect = () => {
      setCurrent(api.selectedScrollSnap() + 1);
    };
    api.on("select", onSelect);
    return () => {
      api.off?.("select", onSelect);
    };
  }, [api]);
  const progressValue = count > 0 ? current / count * 100 : 0;
  return <section data-section-id="117500" id="stories-of-care-and-change" className="bg-background py-12 sm:py-16 md:py-20 lg:py-24 text-foreground overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6">
        <div ref={ref} className={cn("mx-auto max-w-6xl transition-all duration-1000 ease-out", isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0")}>
          <div className="mx-auto mb-10 max-w-3xl text-center sm:mb-12 md:mb-16">
            <h2 className="mb-4 text-3xl sm:text-4xl md:text-5xl font-serif font-bold tracking-tight text-primary">
              Stories of Care and Change
            </h2>
            <p className="text-base sm:text-lg leading-7 sm:leading-8 text-muted-foreground">
              The impact of our work is reflected in the lives of the people and
              communities we support. These stories highlight the value of
              practical help, educational opportunity, and shared hope.
            </p>
          </div>

          <div className="relative mx-auto max-w-5xl">
            <Carousel setApi={setApi} opts={{
            align: "start",
            loop: true
          }} className="w-full">
              <CarouselContent>
                {testimonials.map((testimonial, index) => <CarouselItem key={testimonial.id}>
                    <div className="h-full">
                      <div data-index={index} className="relative h-full rounded-3xl border border-primary/10 bg-card p-6 shadow-sm transition-all duration-500 sm:p-8 md:p-10">
                        <div className="mb-6 flex items-start justify-between gap-4">
                          <div className="flex items-center gap-4">
                            <SafeHydrate><Avatar className="h-14 w-14 sm:h-16 sm:w-16 border-2 border-secondary/30">
                              <AvatarImage src={testimonial.image} alt={testimonial.author} className="object-cover" />
                              <AvatarFallback className="bg-accent text-primary font-bold">
                                {testimonial.author.split(" ").map(n => n[0]).join("")}
                              </AvatarFallback>
                            </Avatar></SafeHydrate>

                            <div>
                              <h3 className="text-base sm:text-lg font-bold text-primary leading-tight">
                                {testimonial.author}
                              </h3>
                              <p className="text-sm text-muted-foreground">
                                {testimonial.role}
                              </p>
                              <p className="mt-1 flex items-center gap-1 text-xs sm:text-sm text-muted-foreground">
                                <MapPin className="h-3.5 w-3.5 shrink-0 text-secondary" />
                                <span>{testimonial.location}</span>
                              </p>
                            </div>
                          </div>

                          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-secondary/10 shrink-0">
                            <Quote className="h-5 w-5 text-secondary" />
                          </div>
                        </div>

                        <blockquote className="text-base sm:text-lg md:text-xl leading-8 text-foreground/85 font-medium">
                          “{testimonial.quote}”
                        </blockquote>
                      </div>
                    </div>
                  </CarouselItem>)}
              </CarouselContent>

              <div className="mt-6 flex items-center justify-between gap-4">
                <div className="flex-1">
                  <Progress value={progressValue} className="h-2" />
                  <p className="mt-2 text-xs sm:text-sm text-muted-foreground">
                    Story {current} of {count}
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <CarouselPrevious className="static translate-y-0" />
                  <CarouselNext className="static translate-y-0" />
                </div>
              </div>
            </Carousel>
          </div>
        </div>
      </div>
    </section>;
}